import React, { useEffect, useRef } from 'react';
import { AVAILABLE_MAP_ZONES } from './constants';
import type { MarketZoneId, MarketZone } from '@app/common/static-data/energy-markets';

interface ZonesListProps {
  selectedZones: MarketZoneId[];
  onZonesChange: (zones: MarketZoneId[]) => void;
  zonesLoaded: boolean;
  availableZones?: MarketZone[];
}

const ZonesList: React.FC<ZonesListProps> = ({
  selectedZones,
  onZonesChange,
  zonesLoaded,
  availableZones,
}) => {
  const selectedZoneRef = useRef<HTMLDivElement>(null);

  // Scroll to selected zone when selection changes
  useEffect(() => {
    if (selectedZones.length > 0 && selectedZoneRef.current) {
      selectedZoneRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedZones.join(',')]);

  if (!zonesLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading map zones...</div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className="p-3">
        <div className="space-y-1">
          {(availableZones || AVAILABLE_MAP_ZONES).map((zone) => {
            const isSelected = selectedZones.includes(zone.id as MarketZoneId);

            return (
              <div
                key={zone.id}
                ref={isSelected ? selectedZoneRef : null}
                onClick={() => {
                  const next = isSelected
                    ? selectedZones.filter((z) => z !== (zone.id as MarketZoneId))
                    : [...selectedZones, zone.id as MarketZoneId];
                  onZonesChange(next);
                }}
                className={`
                  flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors
                  ${
                    isSelected
                      ? 'bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800'
                      : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
                  }
                `}
              >
                <div className="flex-1">
                  <div
                    className={`font-medium text-sm ${
                      isSelected
                        ? 'text-pink-900 dark:text-pink-100'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {zone.label}
                  </div>
                  <div
                    className={`text-xs ${
                      isSelected
                        ? 'text-pink-600 dark:text-pink-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {zone.id}
                  </div>
                </div>
                {isSelected && <div className="w-2 h-2 bg-pink-500 rounded-full"></div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ZonesList;
